/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useState, useMemo } from 'react'

// Router
import { useLocation, useNavigate, useParams } from 'react-router-dom'

// Services
import { useGetPreferenceCardById } from 'lib/services/api/reports-service/preference-card/getPreferenceCardById'
import { useGetSurgeons } from 'lib/services/api/reports-service/preference-card/getSurgeons'
import { useGetProcedureTypes } from 'lib/services/api/reports-service/preference-card/getProcedureTypes'
import { useCreatePrefCard } from 'lib/services/api/reports-service/preference-card/createPreferenceCard'
import { useUpdatePrefCard } from 'lib/services/api/reports-service/preference-card/updatePreferenceCard'
import {
  useGetRepsQuery,
  useSendProductRepInviteMutation,
  useGetOrgConfigQuery,
} from 'lib/apollo/hooks'

// Types
import {
  BetterIdAsset,
  Measurement,
  Option,
  RecordCompanyRep,
  CustomChangeEvent,
} from 'common/types'
import {
  ConsumablesProps,
  EquipmentProps,
  InputCategory,
  InstrumentTraysProps,
  PreferenceCardProps,
  ProcedureDetails,
  SurgeonInformation,
  MedicationTypes,
  Medication,
  PositioningDevices,
  SkinPreps,
  AnesthesiaTypes,
  ConsumableItem,
  ProductItem,
  TagCategory,
  MedicationTiming,
  SelectedMedication,
  SalesRepsOption,
  ISalesRepModal,
} from './CardView.types'
import { PositionChangeProps } from '../PreferenceCards.types'

// Components
import { IconButton, TableRowProps, Typography, Tooltip } from '@mui/material'
import { Add, Delete, Tune } from '@mui/icons-material'
import { MinusOutlined } from '@ant-design/icons'
import { TagSelector } from './CardViewComponents/TagSelector'

// Other
import { debounce } from 'lodash'
import { enqueueSnackbar } from 'notistack'
import { v4 as uuidv4 } from 'uuid'

// Utils
import { useIsMobile } from 'lib/utils/mediaQueries'
import { useSearchConsumables } from 'lib/services/api/product-service/searchConsumables'
import useFindProductByIds from 'lib/services/api/product-service/findProductsByIds'
import formatFraction from 'lib/utils/formatFraction'

// Constants
import {
  defaultTags,
  instrumentTrayOptions,
  pascInstrumentTrayOptions,
} from './constants'
import { useOrganizationSettings } from 'lib/context/OrganizationSettingsContext'
import { useApolloClients } from 'lib/apollo/apolloProvider'

const useCardViewLogic = ({ mode }: { mode: 'view' | 'create' | 'clone' }) => {
  const navigate = useNavigate()
  const { showPascPreferenceCardFeatures, allowFractionalQuantities } =
    useOrganizationSettings()

  const { id } = useParams()
  const { state } = useLocation() as {
    state?: {
      returnToSurgery?: boolean
      surgeryId?: string
      surgeonId?: string
      procedureType?: string
    }
  }

  const isViewMode = mode === 'view'
  const isCreateMode = mode === 'create'
  const isMobile = useIsMobile()
  const DELAY = 1000

  // States
  const [preferenceCard, setPreferenceCard] = useState<PreferenceCardProps>({
    id: id ?? '',
    name: null,
  })
  const [implantDescription, setImplantDescription] = useState('')
  const [biologicDescription, setBiologicDescription] = useState('')
  const [showPreferenceCardNameError, setShowPreferenceCardNameError] =
    useState(false)
  const [consumablesSearchValue, setConsumablesSearchValue] = useState('')
  const [surgeonInformation, setSurgeonInformation] =
    useState<SurgeonInformation>()
  const [procedureDetails, setProcedureDetails] = useState<ProcedureDetails>({
    tableRotationSide: 'Left',
    skinPrep: {
      betadine10: false,
      betadine5: false,
      chloraPrep: false,
      chlorhexidine: false,
      dilutedBetadine: false,
      PCMX: false,
    },
    positioningDevices: {
      gelDonut: false,
      gelRolls: false,
      other: false,
      otherText: '',
    },
    anesthesia: {
      interscalene: false,
      supraclavicular: false,
      infraclavicular: false,
      axillary: false,
      femoral: false,
      adductorCanal: false,
      sciatic: false,
      popliteal: false,
      ankle: false,
    },
    medication: {
      lidocaine: {
        concentration: undefined,
        epiConcentration: undefined,
        volume: '',
      },
      lidocaineWithEpi: {
        concentration: undefined,
        epiConcentration: undefined,
        volume: '',
      },
      marcaine: {
        concentration: undefined,
        epiConcentration: undefined,
        volume: '',
      },
      marcaineWithEpi: {
        concentration: undefined,
        epiConcentration: undefined,
        volume: '',
      },
      other: '',
      selectedMedications: [],
    },
  })

  // Add state for tags
  const [availableTags, setAvailableTags] = useState<TagCategory[]>(defaultTags)
  const [selectedFilterTags, setSelectedFilterTags] = useState<TagCategory[]>(
    []
  )

  const [equipmentItems, setEquipmentItems] = useState<EquipmentProps>({
    options: [],
    notes: [],
  })
  const [instrumentTrays, setInstrumentTrays] = useState<InstrumentTraysProps>()
  const [consumables, setConsumables] = useState<ConsumablesProps>({
    items: [],
    tags: [], // For custom tags
  })
  const [selectedProducts, setSelectedProducts] = useState<{
    implants: ProductItem[]
    biologic: ProductItem[]
  }>({
    biologic: [],
    implants: [],
  })

  const [selectedCompanies, setSelectedCompanies] = useState<{
    implants: Option[]
    biologic: Option[]
  }>({
    biologic: [],
    implants: [],
  })

  const [selectedProduct, setSelectedProduct] = useState<Option | null>(null)
  const [quantityModalOpen, setQuantityModalOpen] = useState(false)
  const [productForMeasurement, setProductForMeasurement] =
    useState<Option | null>(null)
  const [existingMeasurement, setExistingMeasurement] = useState<
    Measurement | undefined
  >(undefined)

  const [medicationSearchValue, setMedicationSearchValue] = useState('')

  // Add state for sales reps
  const [linkedSalesReps, setLinkedSalesReps] = useState<SalesRepsOption[]>([])
  const [selectedVendor, setSelectedVendor] = useState<Option>()
  const [salesRepModal, setSalesRepModal] = useState<ISalesRepModal>({
    isOpen: false,
    type: 'rep',
    modalId: uuidv4(),
  })

  const isNMRepEnabled = equipmentItems?.options?.[0]?.['Neuromonitoring']
  const isPerfusionistRepEnabled = equipmentItems?.options?.[0]?.['Cell Saver']
  const nMRep = linkedSalesReps.find(
    (item) => item.specialty === 'Neuromonitoring'
  )
  const perfusionistRep = linkedSalesReps.find(
    (item) => item.specialty === 'Perfusionist'
  )

  const ConsumableAction: React.FC<{ quantity?: number; option: Option }> = ({
    quantity = 1,
    option,
  }) => {
    const [currentQuantity, setCurrentQuantity] = useState(quantity)
    const item = consumables.items.find(
      (item) => item.bidProductId === option.id
    )
    const measurement = item?.measurement

    const handleIncrease = () => {
      setCurrentQuantity((prevQuantity) => prevQuantity + 1)
      handleConsumablesChange('increase', option, measurement)
    }

    const handleDecrease = () => {
      if (
        measurement?.type === 'fraction' &&
        measurement.numerator &&
        measurement.numerator > 1
      ) {
        handleConsumablesChange('decrease', option, measurement)
      } else {
        if (currentQuantity > 1) {
          setCurrentQuantity((prevQuantity) => prevQuantity - 1)
          handleConsumablesChange('decrease', option, measurement)
        } else {
          handleConsumablesChange('delete', option, measurement)
        }
      }
    }

    return (
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <IconButton onClick={handleDecrease}>
          {measurement?.type === 'fraction' ? (
            measurement.numerator === 1 ? (
              <Tooltip title="Delete Measurement">
                <Delete />
              </Tooltip>
            ) : (
              <Tooltip title="Decrease Measurement">
                <MinusOutlined />
              </Tooltip>
            )
          ) : currentQuantity === 1 ? (
            <Tooltip title="Delete Measurement">
              <Delete />
            </Tooltip>
          ) : (
            <Tooltip title="Decrease Measurement">
              <MinusOutlined />
            </Tooltip>
          )}
        </IconButton>
        <div
          style={{
            margin: '0 10px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Typography variant="body1">
            {measurement?.type === 'fraction'
              ? formatFraction(
                  measurement?.denominator as number,
                  measurement?.numerator as number
                )
              : currentQuantity}
          </Typography>
        </div>
        <IconButton onClick={handleIncrease}>
          <Tooltip title="Increase Measurement">
            <Add />
          </Tooltip>
        </IconButton>
      </div>
    )
  }

  const canSave = useMemo(() => {
    return preferenceCard.name
  }, [preferenceCard.name])

  // Services
  const { iservicesClient } = useApolloClients()
  const {
    data: consumablesProductsData,
    isLoading: isLoadingConsumablesProductsData,
  } = useFindProductByIds(
    preferenceCard.consumables?.items?.map((item) => item.bidProductId) || []
  )

  const {
    data: consumablesData,
    isLoading: isLoadingConsumables,
    isFetching: isFetchingConsumables,
  } = useSearchConsumables({
    search: consumablesSearchValue,
    limit: 20,
    skip: 0,
  })

  const { data: surgeonsData, isLoading: isLoadingSurgeons } = useGetSurgeons()
  const surgeonsOptions = useMemo(
    () =>
      surgeonsData?.surgeons.map((item) => ({
        id: item.id,
        name: `${item.firstName} ${item.lastName}`,
      })),
    [surgeonsData]
  )

  const { data: procedureTypesData, isLoading: isLoadingProcedureTypes } =
    useGetProcedureTypes()
  const procedureTypesOptions = useMemo(
    () =>
      procedureTypesData?.procedures
        ?.filter((item) => item && item !== '-')
        .map((item, index) => ({
          id: index,
          name: item,
        })),
    [procedureTypesData]
  )

  const { data: currentPrefCard, isLoading: isLoadingCard } =
    useGetPreferenceCardById({
      id: id ?? '',
      enabled: Boolean(id),
      disableCache: state?.returnToSurgery,
    })
  const card_details = currentPrefCard?.card_details

  const { mutateAsync: create, isLoading: isCreatingCard } = useCreatePrefCard()
  const { mutateAsync: update, isLoading: isUpdatingCard } = useUpdatePrefCard()

  const [sendProductRepInviteMutation, { loading: isLoadingAddNewRep }] =
    useSendProductRepInviteMutation()

  const { data: orgConfigData } = useGetOrgConfigQuery(iservicesClient)
  const medicationOptions = useMemo(() => {
    if (orgConfigData?.getOrgConfig?.data?.medicationOptions) {
      try {
        const parsedOptions = JSON.parse(
          orgConfigData.getOrgConfig.data.medicationOptions
        )
        return parsedOptions.map((option: any) => ({
          id: option.bidProductId,
          name: option.name,
        })) as Option[]
      } catch (error) {
        console.error('Error parsing medication options:', error)
      }
    }
    return []
  }, [orgConfigData?.getOrgConfig?.data?.medicationOptions])

  const { data: rawSalesReps } = useGetRepsQuery({
    bidCompanyId: selectedVendor?.id ? Number(selectedVendor.id) : undefined,
  })
  const salesReps =
    (rawSalesReps?.getReps.data
      ?.filter((item) => !linkedSalesReps.find((rep) => rep.id === item.id))
      .map(({ id, firstName, lastName, email, mobile, bidCompanyId }) => ({
        id,
        email,
        firstName,
        lastName,
        phoneNumber: mobile,
        companyId: bidCompanyId,
      })) as RecordCompanyRep[]) || []

  const repsOptions: Option[] =
    rawSalesReps?.getReps.data?.map((item) => ({
      name: `${item.lastName}, ${item.firstName}|${item.email}`,
      id: item.id,
    })) || []

  const groupedRepsByCompany = useMemo(() => {
    const allSelectedCompanies = [
      ...selectedCompanies.implants,
      ...selectedCompanies.biologic,
    ]

    return allSelectedCompanies.reduce((acc, company) => {
      const repsForCompany = linkedSalesReps.filter(
        (rep) =>
          String(rep.companyId) === String(company.id) && !rep.isSpecialtyRep
      )
      if (repsForCompany.length > 0) {
        acc[String(company.id)] = {
          companyName: company.name,
          reps: repsForCompany,
        }
      }

      return acc
    }, {} as Record<string, { companyName: string; reps: typeof linkedSalesReps }>)
  }, [selectedCompanies, linkedSalesReps])

  const consumablesOptions =
    consumablesData?.data.products
      .map((item) => ({
        id: item.id,
        name: item.deviceDescription || '',
        catalogNumber: item.catalogNumber || item.versionModelNumber || '',
        manufacturer: item.company?.name || '',
        isMultipack: item.deviceCount > 1,
        originalDescription: item.deviceDescription || '',
        isInCatalog: item.organizationFields?.cost ? true : false,
      }))
      .sort((a, b) => {
        if (a.isInCatalog && !b.isInCatalog) return -1
        if (!a.isInCatalog && b.isInCatalog) return 1
        return 0
      }) || []

  // NEW
  const products = consumablesProductsData?.data?.products

  // Filter consumables by selected tags
  const filteredConsumables = useMemo(() => {
    if (selectedFilterTags.length === 0) {
      return consumables.items
    }
    return consumables.items.filter((item) => {
      if (!item.tags || item.tags.length === 0) return false
      return item.tags.some((tag) => selectedFilterTags.includes(tag))
    })
  }, [consumables.items, selectedFilterTags])

  const items = filteredConsumables?.map((i) => ({
    bidProductId: i.bidProductId,
    qty: i.measurement?.type === 'fraction' ? i.measurement.value : i.qty,
    item: products?.find((p) => p.id === i.bidProductId) as BetterIdAsset,
    tags: i.tags || [],
  }))

  // Import TagSelector at top of file
  const TagsCell = ({
    productId,
    tags = [],
  }: {
    productId: number
    tags?: TagCategory[]
  }) => (
    <TagSelector
      tags={tags}
      availableTags={availableTags}
      onTagsChange={(newTags) => handleTagsChange(productId, newTags)}
      onAddCustomTag={handleAddCustomTag}
    />
  )

  const consumablesTableData = items?.map(
    ({ item, qty, tags, bidProductId }) => {
      const hasMeasurement = consumables.items.find(
        (consumable) => consumable.bidProductId === bidProductId
      )?.measurement

      const rowData: any = {
        description: item?.deviceDescription,
        catalogNumber: item?.catalogNumber || '-',
        modelNumber: item?.versionModelNumber || '-',
        tags: <TagsCell productId={bidProductId} tags={tags} />,
        actions: (
          <ConsumableAction
            quantity={qty}
            option={{ id: item?.id, name: item?.deviceDescription as string }}
          />
        ),
      }

      if (allowFractionalQuantities) {
        rowData.addMeasurement = hasMeasurement ? (
          <Tooltip title="Edit Measurement">
            <IconButton
              size="small"
              onClick={() =>
                handleAddMeasurement({
                  id: item?.id,
                  name: item?.deviceDescription as string,
                })
              }
            >
              <Tune />
            </IconButton>
          </Tooltip>
        ) : (
          <Tooltip title="Add Measurement">
            <IconButton
              size="small"
              onClick={() =>
                handleAddMeasurement({
                  id: item?.id,
                  name: item?.deviceDescription as string,
                })
              }
            >
              <Add />
            </IconButton>
          </Tooltip>
        )
      }

      return rowData
    }
  ) as TableRowProps[]

  // Create dynamic table headers based on feature flag
  const getConsumablesTableHeaders = () => {
    const baseHeaders = [
      {
        id: 'description',
        label: 'Description',
        numeric: true,
      },
      {
        id: 'catalogNumber',
        label: 'Catalog Number',
        numeric: true,
      },
      {
        id: 'modelNumber',
        label: 'Model Number',
        numeric: true,
      },
      {
        id: 'tags',
        label: 'Tags',
        numeric: true,
      },
    ]

    if (allowFractionalQuantities) {
      baseHeaders.push({
        id: 'addMeasurement',
        label: 'Add Measurement',
        numeric: true,
      })
    }

    baseHeaders.push({
      id: 'actions',
      label: 'ACTIONS',
      numeric: true,
    })

    return baseHeaders
  }

  const consumablesTableHeaders = getConsumablesTableHeaders()

  // Consumables table sorting state with persistence
  const [consumablesSortOrder, setConsumablesSortOrder] = useState<
    'asc' | 'desc'
  >('asc')
  const [consumablesSortBy, setConsumablesSortBy] = useState<
    keyof TableRowProps
  >('description' as keyof TableRowProps)

  // Load sorting state from localStorage on mount
  useEffect(() => {
    const savedSortOrder = localStorage.getItem('consumablesTableSortOrder')
    const savedSortBy = localStorage.getItem('consumablesTableSortBy')

    if (savedSortOrder) {
      setConsumablesSortOrder(savedSortOrder as 'asc' | 'desc')
    }
    if (savedSortBy) {
      setConsumablesSortBy(savedSortBy as keyof TableRowProps)
    }
  }, [])

  // Handle consumables table sorting changes
  const handleConsumablesSortChange = useCallback(
    (order: 'asc' | 'desc', orderBy: keyof TableRowProps) => {
      setConsumablesSortOrder(order)
      setConsumablesSortBy(orderBy)

      // Persist to localStorage
      localStorage.setItem('consumablesTableSortOrder', order)
      localStorage.setItem('consumablesTableSortBy', String(orderBy))
    },
    []
  )

  // Handlers
  const handleSurgeonInformationChange = useCallback(
    (field: keyof SurgeonInformation, value: string | string[] | Option) => {
      setSurgeonInformation((prevState) => ({ ...prevState, [field]: value }))
    },
    []
  )

  const handlePositionChange = useCallback(
    (selectedPosition: PositionChangeProps) => {
      setProcedureDetails((prevState) => ({
        ...prevState,
        selectedPosition,
      }))
    },
    []
  )

  const handleInputChange = useCallback(
    (field: keyof ProcedureDetails, value?: string | boolean | string[]) => {
      setProcedureDetails((prevState) => ({ ...prevState, [field]: value }))
    },
    []
  )

  const handleEquipmentItemChange = useCallback(
    (index: number, field: string, value: string | boolean) => {
      setEquipmentItems((prev) => {
        const updatedItems = [...(prev.options || [])]
        if (!updatedItems[index]) {
          updatedItems[index] = {}
        }
        updatedItems[index][field] = value
        return { ...prev, options: updatedItems }
      })
    },
    []
  )

  const handleClickBack = useCallback(() => {
    if (state?.returnToSurgery && state?.surgeryId) {
      navigate(`/surgeries/${state.surgeryId}`)
    } else {
      navigate('/preference-cards')
    }
  }, [navigate, state?.returnToSurgery, state?.surgeryId])

  const handlePositioningDeviceChange = useCallback(
    (device: keyof PositioningDevices, checked: boolean) => {
      setProcedureDetails((prevState) => ({
        ...prevState,
        positioningDevices: {
          ...prevState.positioningDevices,
          [device]: checked,
          ...(device === 'other' && !checked ? { otherText: '' } : {}),
        },
      }))
    },
    []
  )

  const handlePositioningDeviceOtherText = useCallback((text: string) => {
    setProcedureDetails((prevState) => ({
      ...prevState,
      positioningDevices: {
        ...prevState.positioningDevices,
        otherText: text,
      },
    }))
  }, [])

  const handleSkinPrepChange = useCallback(
    (device: keyof SkinPreps, checked: boolean) => {
      setProcedureDetails((prevState) => ({
        ...prevState,
        skinPrep: {
          ...prevState.skinPrep,
          [device]: checked,
        },
      }))
    },
    []
  )

  const handleAnesthesiaChange = useCallback(
    (type: keyof AnesthesiaTypes, checked: boolean) => {
      setProcedureDetails((prevState) => ({
        ...prevState,
        anesthesia: {
          ...prevState.anesthesia,
          [type]: checked,
        },
      }))
    },
    []
  )

  const handleMedicationsChange = (
    medName: keyof MedicationTypes,
    field: keyof Medication | 'other',
    value: string
  ) => {
    setProcedureDetails((prevState) => {
      const currentMedication = prevState.medication ?? {
        lidocaine: {
          concentration: undefined,
          epiConcentration: undefined,
          volume: '',
        },
        lidocaineWithEpi: {
          concentration: undefined,
          epiConcentration: undefined,
          volume: '',
        },
        marcaine: {
          concentration: undefined,
          epiConcentration: undefined,
          volume: '',
        },
        marcaineWithEpi: {
          concentration: undefined,
          epiConcentration: undefined,
          volume: '',
        },
        other: '',
        selectedMedications: [],
      }

      if (medName === 'other') {
        return {
          ...prevState,
          medication: {
            ...currentMedication,
            other: value,
          },
        }
      }

      return {
        ...prevState,
        medication: {
          ...currentMedication,
          [medName]: {
            ...currentMedication[medName],
            [field]: value,
          },
        },
      }
    })
  }

  const resetMedication = (medName: keyof MedicationTypes) => {
    setProcedureDetails((prevState) => {
      const currentMedication = prevState.medication ?? {
        lidocaine: {
          concentration: undefined,
          epiConcentration: undefined,
          volume: '',
        },
        lidocaineWithEpi: {
          concentration: undefined,
          epiConcentration: undefined,
          volume: '',
        },
        marcaine: {
          concentration: undefined,
          epiConcentration: undefined,
          volume: '',
        },
        marcaineWithEpi: {
          concentration: undefined,
          epiConcentration: undefined,
          volume: '',
        },
        other: '',
        selectedMedications: [],
      }

      if (medName === 'other') {
        return {
          ...prevState,
          medication: {
            ...currentMedication,
            other: '',
          },
        }
      }

      // Reset fields for the selected medication
      return {
        ...prevState,
        medication: {
          ...currentMedication,
          [medName]: {
            concentration: undefined,
            epiConcentration: undefined,
            volume: '',
          },
        },
      }
    })
  }

  const handleDeleteCompany = (type: InputCategory, item: Option) => {
    setLinkedSalesReps((prev) =>
      prev.filter((rep) => rep.companyId !== item.id)
    )
    setSelectedCompanies((prevCompanies) => {
      if (type === 'biologic') {
        const updatedBiologicCompanies = prevCompanies.biologic.filter(
          (company) => company.id !== item.id
        )

        return {
          ...prevCompanies,
          biologic: updatedBiologicCompanies,
        }
      }

      if (type === 'implants') {
        const updatedImplantCompanies = prevCompanies.implants.filter(
          (company) => company.id !== item.id
        )

        return {
          ...prevCompanies,
          implants: updatedImplantCompanies,
        }
      }
      return prevCompanies
    })
  }

  const handleDeleteProduct = (type: InputCategory, item: ProductItem) => {
    setSelectedProducts((prevProducts) => ({
      ...prevProducts,
      [type]: prevProducts[type].filter((product) => product.id !== item.id),
    }))
  }

  const handleAddCompanies = (type: InputCategory, item: Option) => {
    if (!item) return

    setSelectedCompanies((prevCompanies) => {
      if (type === 'biologic') {
        const isUniqueCompany = !prevCompanies.biologic.some(
          (company) => company.id === item.id
        )

        return {
          ...prevCompanies,
          biologic: isUniqueCompany
            ? [...prevCompanies.biologic, item]
            : prevCompanies.biologic,
        }
      }

      if (type === 'implants') {
        const isUniqueCompany = !prevCompanies.implants.some(
          (company) => company.id === item.id
        )

        return {
          ...prevCompanies,
          implants: isUniqueCompany
            ? [...prevCompanies.implants, item]
            : prevCompanies.implants,
        }
      }

      return prevCompanies
    })
  }
  const handleAddProducts = (type: InputCategory, item: ProductItem) => {
    setSelectedProducts((prevProducts) => ({
      ...prevProducts,
      [type]: [...prevProducts[type], item],
    }))
  }

  const handleNotifySalesRep = (
    email: string,
    bool: boolean,
    type: 'EMAIL' | 'SMS'
  ) => {
    setLinkedSalesReps((prev) =>
      prev.map((rep) => {
        if (rep.email === email) {
          return {
            ...rep,
            sendEmail: type === 'EMAIL' ? bool : rep.sendEmail,
            sendSms: type === 'SMS' ? bool : rep.sendSms,
          }
        }
        return rep
      })
    )
  }

  const handleAddSalesRep = (e: CustomChangeEvent) => {
    if (!e.target.value) return
    const rep = e.target.value as Option
    const selectedRep = salesReps.find((item) => item.id === rep.id)
    if (!selectedRep) return

    setLinkedSalesReps((prev) => {
      const newSalesRep = {
        ...({
          ...selectedRep,
          sendEmail: true,
          sendSms: true,
          name: `${selectedRep.firstName} ${selectedRep.lastName}`,
        } as SalesRepsOption),
      }
      const isDuplicate = prev.some((rep) => rep.id === newSalesRep.id)

      if (isDuplicate) return prev

      return [...prev, newSalesRep]
    })
  }

  const handleAddNewSalesRep = (newRep: SalesRepsOption) => {
    if (salesRepModal.type === 'rep') {
      const {
        email,
        companyId,
        phoneNumber,
        companyName,
        firstName,
        lastName,
      } = newRep
      sendProductRepInviteMutation({
        variables: {
          bidCompanyId: companyId as number,
          email,
          firstName,
          lastName,
          mobile: phoneNumber as string,
          accessLevel: 1,
          companyName: companyName as string,
          resultUrl: window.location.origin,
        },
      })
        .then((res) => {
          if (res.data?.sendProductRepInvite.success) {
            setLinkedSalesReps((prev) => [...prev, newRep])
            setSalesRepModal({ isOpen: false, modalId: uuidv4() })
            enqueueSnackbar('Invitation sent successfully to sales rep.', {
              variant: 'success',
            })
          }
        })
        .catch(() => {
          enqueueSnackbar(
            'Something wrong happened while inviting the sales rep.',
            { variant: 'error' }
          )
        })
    } else {
      setLinkedSalesReps((prev) => {
        const isDuplicate = prev.some((rep) => rep.email === newRep.email)

        if (isDuplicate) {
          enqueueSnackbar('This email already exists.', { variant: 'error' })
          return prev
        }

        return [...prev, newRep]
      })
      setSalesRepModal({ isOpen: false, modalId: uuidv4() })
    }
  }

  const handleConsumablesChange = (
    action: 'add' | 'delete' | 'increase' | 'decrease',
    option: Option,
    measurement?: Measurement
  ) => {
    setConsumables((prev) => {
      const currentItems = prev?.items || []

      switch (action) {
        case 'add': {
          if (!option.id) return { items: currentItems }
          const existingItem = currentItems.find(
            (item) => item.bidProductId === option.id
          )

          if (existingItem) {
            const updatedItems = currentItems.map((item) =>
              item.bidProductId === option.id
                ? { ...item, qty: item.qty + 1 }
                : item
            )
            return { items: updatedItems }
          } else {
            const foundProduct = consumablesData?.data.products.find(
              (item) => item.id === option.id
            )
            if (!foundProduct) return { items: currentItems }

            const newItem: ConsumableItem = {
              bidProductId: Number(option.id),
              qty: 1,
            }

            if (measurement) {
              newItem.measurement = measurement
              newItem.qty = Number(measurement.value.toFixed(2))
            }

            return {
              items: [...currentItems, newItem],
            }
          }
        }

        case 'delete':
          return {
            items: currentItems.filter(
              (item) => item.bidProductId !== option.id
            ),
          }

        case 'increase': {
          const updatedItems = currentItems.map((item) => {
            if (item.bidProductId === option.id) {
              if (
                item.measurement?.type === 'fraction' &&
                item.measurement?.denominator &&
                item.measurement?.numerator
              ) {
                const denominator = item.measurement.denominator
                const numerator = item.measurement?.numerator
                  ? item.measurement.numerator + 1
                  : 1
                const unit = `${numerator}/${denominator}`
                return {
                  ...item,
                  measurement: {
                    type: 'fraction',
                    value: Number((numerator / denominator).toFixed(2)),
                    unit: unit,
                    numerator: numerator,
                    denominator: denominator,
                  } as Measurement,
                  qty: Number((numerator / denominator).toFixed(2)),
                }
              } else {
                return {
                  ...item,
                  qty: item.qty + 1,
                }
              }
            }
            return item
          })
          return { items: updatedItems }
        }

        case 'decrease': {
          const updatedItems = currentItems.map((item) => {
            if (item.bidProductId === option.id) {
              if (
                item.measurement?.type === 'fraction' &&
                item.measurement?.denominator &&
                item.measurement?.numerator
              ) {
                const denominator = item.measurement.denominator
                const numerator = item.measurement.numerator - 1
                const unit = `${numerator}/${denominator}`
                return {
                  ...item,
                  measurement: {
                    type: 'fraction',
                    value: Number((numerator / denominator).toFixed(2)),
                    unit: unit,
                    numerator: numerator,
                    denominator: denominator,
                  } as Measurement,
                  qty: Number((numerator / denominator).toFixed(2)),
                }
              } else {
                return { ...item, qty: item.qty - 1 }
              }
            }
            return item
          })
          return { items: updatedItems }
        }

        default:
          return { items: currentItems }
      }
    })
  }

  const debouncedSetSearchValue = useCallback(
    debounce((value: string) => {
      setConsumablesSearchValue(value)
    }, 500),
    []
  )

  // Retrieve the current preference card logic
  useEffect(() => {
    if (currentPrefCard) {
      setPreferenceCard({
        ...currentPrefCard,
        ...currentPrefCard?.card_details,
        name: currentPrefCard.name,
      })
      const surgeon = surgeonsOptions?.find(
        (item) => item.id === currentPrefCard?.surgeon?.id
      )

      setSurgeonInformation({
        ...card_details?.surgeonInformation,
        physicianName: surgeon,
        procedureName: currentPrefCard.procedure_type,
      })

      setProcedureDetails(card_details?.procedureDetails ?? {})
      setEquipmentItems(card_details?.equipment ?? { options: [] })
      setInstrumentTrays(card_details?.instrumentTrays ?? {})
      setConsumables(card_details?.consumables ?? { items: [] })
      setLinkedSalesReps(card_details?.linkedSalesReps ?? [])
      setSelectedCompanies({
        biologic: card_details?.companies?.biologic || [],
        implants: card_details?.companies?.implants || [],
      })
      const uniqueTags = [
        ...new Set([
          ...defaultTags,
          ...(card_details?.consumables?.tags ?? []),
        ]),
      ]
      setAvailableTags(uniqueTags)
      setSelectedProducts(
        card_details?.products ?? { biologic: [], implants: [] }
      )
    }
  }, [currentPrefCard])

  // Retrieve the surgeon information from state
  // Create new pref card from procedures list logic
  useEffect(() => {
    if (state && isCreateMode) {
      const { surgeonId, procedureType } = state
      if (surgeonId && procedureType) {
        const surgeon = surgeonsOptions?.find((item) => item.id === surgeonId)
        setSurgeonInformation({
          physicianName: surgeon,
          procedureName: procedureType,
        })
      }
    }
  }, [state, surgeonsOptions, isCreateMode])

  // Clone mode effect
  useEffect(() => {
    if (mode === 'clone' && currentPrefCard) {
      // Get the card data but remove sensitive fields
      const clonedCard = {
        ...currentPrefCard,
        ...currentPrefCard?.card_details,
        name: null,
        surgeon: undefined,
        procedure_type: undefined,
        picture: undefined,
      }

      // Set the base card data
      setPreferenceCard(clonedCard)

      // Set other data but remove sensitive information
      setSurgeonInformation({
        ...card_details?.surgeonInformation,
        physicianName: undefined,
        procedureName: undefined,
        picture: undefined,
      })

      // Set the rest of the data as is
      setProcedureDetails(card_details?.procedureDetails ?? {})
      setEquipmentItems(card_details?.equipment ?? { options: [] })
      setInstrumentTrays(card_details?.instrumentTrays ?? {})
      setConsumables(card_details?.consumables ?? { items: [] })
      setLinkedSalesReps(card_details?.linkedSalesReps ?? [])
      setSelectedCompanies({
        biologic: card_details?.companies?.biologic || [],
        implants: card_details?.companies?.implants || [],
      })
      const uniqueTags = [
        ...new Set([
          ...defaultTags,
          ...(card_details?.consumables?.tags ?? []),
        ]),
      ]
      setAvailableTags(uniqueTags)
      setSelectedProducts(
        card_details?.products ?? { biologic: [], implants: [] }
      )
    }
  }, [mode, currentPrefCard, card_details])

  // Updating logic
  const savePreferenceCards = debounce((data: PreferenceCardProps) => {
    if (isViewMode && preferenceCard.name) {
      update({
        id: id as string,
        card_details: data,
        name: preferenceCard.name,
      }).catch((error) => {
        console.error('ERROR', error)
      })
    }
  }, DELAY)

  const debouncedSavePreferenceCards = debounce(savePreferenceCards, DELAY)
  useEffect(() => {
    if (
      !preferenceCard.name &&
      isViewMode &&
      !isLoadingConsumables &&
      !isFetchingConsumables &&
      !isLoadingCard
    ) {
      setShowPreferenceCardNameError(true)
      return
    }
    setShowPreferenceCardNameError(false)
    const updatedPreferenceCard: PreferenceCardProps = {
      id: id ?? '',
      name: preferenceCard.name,
      surgeonInformation,
      procedureDetails,
      equipment: equipmentItems,
      instrumentTrays,
      consumables: {
        ...consumables,
        tags: availableTags,
      },
      linkedSalesReps,
      companies: selectedCompanies,
      products: selectedProducts,
    }
    setPreferenceCard(updatedPreferenceCard)
    debouncedSavePreferenceCards(updatedPreferenceCard)

    return () => {
      debouncedSavePreferenceCards.cancel()
    }
  }, [
    id,
    preferenceCard.name,
    surgeonInformation,
    procedureDetails,
    equipmentItems,
    instrumentTrays,
    consumables,
    linkedSalesReps,
    selectedCompanies,
    selectedProducts,
    selectedFilterTags,
    availableTags,
  ])

  // Creating logic
  const createPreferenceCard = () => {
    if (!preferenceCard.name) {
      setShowPreferenceCardNameError(true)
      return
    }
    setShowPreferenceCardNameError(false)
    const data: PreferenceCardProps = {
      id: id ?? '',
      name: preferenceCard.name,
      surgeonInformation,
      procedureDetails,
      equipment: equipmentItems,
      instrumentTrays,
      consumables: {
        ...consumables,
        tags: availableTags,
      },
      linkedSalesReps,
      companies: selectedCompanies,
      products: selectedProducts,
    }

    create({
      name: preferenceCard.name,
      surgeon_id: surgeonInformation?.physicianName?.id as string,
      procedure_type: surgeonInformation?.procedureName ?? '',
      card_details: data,
    })
      .then(() => {
        navigate('/preference-cards')
      })
      .catch((error) => {
        enqueueSnackbar(error?.response?.data?.error, {
          variant: 'error',
        })
      })
  }

  const handleProductSelect = (option: Option) => {
    if (allowFractionalQuantities) {
      handleConsumablesChange('add', option)
    } else {
      handleConsumablesChange('add', option)
    }
  }

  const handleAddMeasurement = (option: Option) => {
    const existingItem = consumables.items.find(
      (item) => item.bidProductId === option.id
    )

    setProductForMeasurement(option)
    setExistingMeasurement(existingItem?.measurement)
    setQuantityModalOpen(true)
  }

  const handleMeasurementConfirm = (measurement: Measurement) => {
    if (productForMeasurement) {
      setConsumables((prev) => {
        const currentItems = prev?.items || []
        const updatedItems = currentItems.map((item) => {
          if (item.bidProductId === productForMeasurement.id) {
            return {
              ...item,
              measurement: measurement,
              qty: Number(measurement.value.toFixed(2)),
            }
          }
          return item
        })
        return { ...prev, items: updatedItems }
      })
      setProductForMeasurement(null)
      setExistingMeasurement(undefined)
      setQuantityModalOpen(false)
    } else if (selectedProduct) {
      handleConsumablesChange('add', selectedProduct, measurement)
      setSelectedProduct(null)
      setQuantityModalOpen(false)
    }
  }

  const handleInputNameChange = (value: string) => {
    setPreferenceCard((prev) => ({ ...prev, name: value }))
  }

  const handleTagsChange = (productId: number, tags: TagCategory[]) => {
    setConsumables((prev) => {
      const updatedItems = prev.items.map((item) => {
        if (item.bidProductId === productId) {
          return { ...item, tags }
        }
        return item
      })
      return { ...prev, items: updatedItems }
    })
  }

  const handleAddCustomTag = (tag: string) => {
    if (!consumables.tags?.includes(tag)) {
      setConsumables((prev) => ({
        ...prev,
        tags: [...(prev.tags || []), tag],
      }))
      setAvailableTags((prev) => [...prev, tag])
    }
  }

  const handleTagFilterSelect = (tag: TagCategory) => {
    setSelectedFilterTags((prev) => {
      if (prev.includes(tag)) {
        return prev.filter((t) => t !== tag)
      } else {
        return [...prev, tag]
      }
    })
  }

  const handleMedicationSelect = (option: Option) => {
    if (option.id === null) {
      addMedicationToTable({
        id: uuidv4(),
        name: option.name,
      })
      setMedicationSearchValue('')
      return
    }

    if (!option.id) {
      return
    }

    // Instead of setting the selected medication, add it immediately to the table
    addMedicationToTable(option)

    // Add as a consumable item
    setConsumables((prev) => ({
      ...prev,
      items: [
        ...(prev.items || []),
        { bidProductId: Number(option.id), qty: 1, measurement: undefined },
      ],
    }))

    // Clear search after selection
    setMedicationSearchValue('')
  }
  const addMedicationToTable = (medication: Option) => {
    if (!medication) return

    setProcedureDetails((prev) => {
      const currentMedication = prev.medication || {
        lidocaine: {
          concentration: undefined,
          epiConcentration: undefined,
          volume: '',
        },
        lidocaineWithEpi: {
          concentration: undefined,
          epiConcentration: undefined,
          volume: '',
        },
        marcaine: {
          concentration: undefined,
          epiConcentration: undefined,
          volume: '',
        },
        marcaineWithEpi: {
          concentration: undefined,
          epiConcentration: undefined,
          volume: '',
        },
        other: '',
        selectedMedications: [],
      }

      const existingMedIndex = currentMedication.selectedMedications?.findIndex(
        (med) => med.id === medication.id
      )

      if (existingMedIndex !== undefined && existingMedIndex >= 0) {
        return prev
      }

      const newMedication: SelectedMedication = {
        id: medication.id as string,
        name: medication.name as string,
        timing: 'On the field',
      }

      const existingMedications = currentMedication.selectedMedications || []

      return {
        ...prev,
        medication: {
          ...currentMedication,
          selectedMedications: [newMedication, ...existingMedications],
        },
      }
    })
  }

  const handleRemoveMedication = (medicationId: string) => {
    setProcedureDetails((prev) => {
      const currentMedication = prev.medication || {
        lidocaine: {
          concentration: undefined,
          epiConcentration: undefined,
          volume: '',
        },
        lidocaineWithEpi: {
          concentration: undefined,
          epiConcentration: undefined,
          volume: '',
        },
        marcaine: {
          concentration: undefined,
          epiConcentration: undefined,
          volume: '',
        },
        marcaineWithEpi: {
          concentration: undefined,
          epiConcentration: undefined,
          volume: '',
        },
        other: '',
        selectedMedications: [],
      }

      const updatedMedications =
        currentMedication.selectedMedications?.filter(
          (med) => med.id !== medicationId
        ) || []

      return {
        ...prev,
        medication: {
          ...currentMedication,
          selectedMedications: updatedMedications,
        },
      }
    })
  }

  const handleChangeMedicationTiming = (
    medicationId: string,
    timing: MedicationTiming
  ) => {
    setProcedureDetails((prev) => {
      const currentMedication = prev.medication || {
        lidocaine: {
          concentration: undefined,
          epiConcentration: undefined,
          volume: '',
        },
        lidocaineWithEpi: {
          concentration: undefined,
          epiConcentration: undefined,
          volume: '',
        },
        marcaine: {
          concentration: undefined,
          epiConcentration: undefined,
          volume: '',
        },
        marcaineWithEpi: {
          concentration: undefined,
          epiConcentration: undefined,
          volume: '',
        },
        other: '',
        selectedMedications: [],
      }

      const updatedMedications =
        currentMedication.selectedMedications?.map((med) =>
          med.id === medicationId ? { ...med, timing } : med
        ) || []

      return {
        ...prev,
        medication: {
          ...currentMedication,
          selectedMedications: updatedMedications,
        },
      }
    })
  }

  const handleChangeMedicationVolume = (
    medicationId: string,
    volume: string
  ) => {
    setProcedureDetails((prev) => {
      const currentMedication = prev.medication || {
        lidocaine: {
          concentration: undefined,
          epiConcentration: undefined,
          volume: '',
        },
        lidocaineWithEpi: {
          concentration: undefined,
          epiConcentration: undefined,
          volume: '',
        },
        marcaine: {
          concentration: undefined,
          epiConcentration: undefined,
          volume: '',
        },
        marcaineWithEpi: {
          concentration: undefined,
          epiConcentration: undefined,
          volume: '',
        },
        other: '',
        selectedMedications: [],
      }

      const updatedMedications =
        currentMedication.selectedMedications?.map((med) =>
          med.id === medicationId ? { ...med, volume } : med
        ) || []

      return {
        ...prev,
        medication: {
          ...currentMedication,
          selectedMedications: updatedMedications,
        },
      }
    })
  }

  const debouncedSetMedicationSearchValue = useCallback((value: string) => {
    setMedicationSearchValue(value)
  }, [])

  const handleDeleteSalesRep = (item: SalesRepsOption) => {
    setLinkedSalesReps((prev) => prev.filter((rep) => rep.email !== item.email))
  }

  return {
    nMRep,
    canSave,
    isMobile,
    repsOptions,
    consumables,
    isLoading: isCreatingCard || isUpdatingCard || isLoadingCard,
    medications: procedureDetails.medication,
    availableTags,
    isLoadingCard,
    salesRepModal,
    isNMRepEnabled,
    preferenceCard,
    equipmentItems,
    isCreatingCard,
    selectedProduct,
    productForMeasurement,
    existingMeasurement,
    selectedVendor,
    isUpdatingCard,
    surgeonsOptions,
    perfusionistRep,
    instrumentTrays,
    selectedProducts,
    procedureDetails,
    selectedCompanies,
    quantityModalOpen,
    medicationOptions,
    isLoadingSurgeons,
    consumablesOptions,
    isLoadingAddNewRep,
    surgeonInformation,
    selectedFilterTags,
    implantDescription,
    biologicDescription,
    isLoadingConsumables: isLoadingConsumables || isFetchingConsumables,
    consumablesTableData,
    consumablesTableHeaders,
    groupedRepsByCompany,
    medicationSearchValue,
    procedureTypesOptions,
    consumablesSearchValue,
    isLoadingProcedureTypes,
    instrumentTrayOptions: showPascPreferenceCardFeatures
      ? pascInstrumentTrayOptions
      : instrumentTrayOptions,
    debouncedSetSearchValue,
    isPerfusionistRepEnabled,
    showPreferenceCardNameError,
    showPascPreferenceCardFeatures,
    isLoadingConsumablesProductsData,
    navigate,
    handleClickBack,
    resetMedication,
    setSalesRepModal,
    handleInputChange,
    handleAddProducts,
    setEquipmentItems,
    handleAddCompanies,
    setSelectedVendor,
    handleAddSalesRep,
    setInstrumentTrays,
    handleProductSelect,
    handleDeleteProduct,
    handleDeleteCompany,
    handlePositionChange,
    handleNotifySalesRep,
    handleSkinPrepChange,
    handleAddNewSalesRep,
    setQuantityModalOpen,
    setProductForMeasurement,
    setExistingMeasurement,
    createPreferenceCard,
    handleDeleteSalesRep,
    handleInputNameChange,
    handleTagFilterSelect,
    setImplantDescription,
    setBiologicDescription,
    handleMedicationSelect,
    handleRemoveMedication,
    handleMedicationsChange,
    handleMeasurementConfirm,
    handleEquipmentItemChange,
    handleChangeMedicationTiming,
    handleChangeMedicationVolume,
    handlePositioningDeviceChange,
    handleSurgeonInformationChange,
    handlePositioningDeviceOtherText,
    handleAnesthesiaChange,
    debouncedSetMedicationSearchValue,
    consumablesSortOrder,
    consumablesSortBy,
    handleConsumablesSortChange,
  }
}
export default useCardViewLogic
